import React, { Fragment, useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Helmet from 'react-helmet';

import { getCategories } from '../actions/categoryLanding';

import { default as navigation } from '../navigation';

import HomeNav from '../components/HomeNav.jsx';
import { sorts } from '../utils';

import Fuse from 'fuse.js';
import Input from '../components/form/FormikInput';
import { useFormik } from 'formik';
import { useAddCategoryMutation, useLazyGetCategoryQuery } from '../services/category';
import { object, string } from 'yup';
import Modal from '../components/Modal';
import Alert from 'react-s-alert';
import { useSelector } from 'react-redux';
import { UnAuthView } from './CategoryView';
import { userPermissions } from './UserDashboard';

const CatSearch = ({ term = '', onReset = () => {}, onSearch = () => {} }) => {
    const { values, handleSubmit, handleChange } = useFormik({
        initialValues: {
            term: term || ''
        },
        enableReinitialize: true,
        onSubmit: (values) => onSearch(values.term)
    });
    return (
        <form className="row bottom-md mb-6" onSubmit={handleSubmit}>
            <div className="col-xs-12 col-md-4">
                <Input id="term" label="Find URL(s)" collapse value={values.term} onChange={handleChange} />
            </div>
            <div className="col-xs-12 col-md-3">
                <button className="button button-primary" type="submit">
                    Search
                </button>
                <button className="button" type="button" onClick={onReset}>
                    Reset
                </button>
            </div>
        </form>
    );
};

const sanitizeName = (value) => {
    if (value[0] === '/') return value.slice(1);
    return value;
};

const AddCategory = ({ onSuccess = () => {}, onCancel = () => {}, onError = () => {} }) => {
    const [addCategory] = useAddCategoryMutation();
    const [getCategory] = useLazyGetCategoryQuery();
    const { values, handleSubmit, handleChange } = useFormik({
        initialValues: {
            category_name: '',
            display_name: ''
        },
        validationSchema: object().shape({
            category_name: string().required(),
            display_name: string()
        }),
        onSubmit: async (values) => {
            let category_name = sanitizeName(values.category_name);
            try {
                let lookup = await getCategory(category_name);
                if (lookup?.data?._id) {
                    throw new Error('Category Already Exists');
                }
                if (lookup?.error?.status !== 404) {
                    throw new Error(lookup?.error?.data?.message || 'Something went wrong creating the category');
                }
                // Does not appear to exist so its OK to create.
                const data = await addCategory({
                    ...values,
                    category_name
                }).unwrap();
                onSuccess(data, true);
            } catch (error) {
                onError(error);
            }
        }
    });
    return (
        <form className="row bottom-md mb-6" onSubmit={handleSubmit}>
            <div className="col-xs-12">
                <Input
                    id="category_name"
                    label="Category URL. Example: wonderbook/favorite-friends/arthur"
                    value={values.category_name}
                    onChange={handleChange}
                />
            </div>
            <div className="col-xs-12">
                <Input
                    id="display_name"
                    label="Category Display Name"
                    value={values.display_name}
                    onChange={handleChange}
                />
            </div>

            <div className="col-xs-12 col-md-3">
                <button className="button button-primary" type="submit">
                    Add Category
                </button>
                <button className="button" type="button" onClick={onCancel}>
                    Cancel
                </button>
            </div>
        </form>
    );
};

const CategoryList = () => {
    const session = useSelector((state) => state.session);
    const isContentAdmin = session?.permissions?.includes(userPermissions.CONTENT_ADMIN);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const term = searchParams.get('term') || '';
    const [categories, setCategories] = useState({
        SHOP: [],
        BOP: []
    });
    const [results, setResults] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);

    const flatCats = useMemo(() => Object.values(categories).flatMap((x) => x), [categories]);

    let catFuse = React.useRef();

    React.useEffect(() => {
        (async () => {
            if (!isContentAdmin) return;
            let sites = await getCategories();
            setCategories(sites);
        })();
    }, [isContentAdmin]);

    React.useEffect(() => {
        if (!term) {
            setResults([]);
            return;
        }
        if (flatCats.length) {
            catFuse.current = new Fuse(flatCats, {
                keys: ['category_name'],
                threshold: 0.4,
                caseSensitive: true
            });
            setResults(catFuse?.current?.search(term || '').map((x) => x.item));
        }
    }, [flatCats, term]);

    let groupCategories = (categories) => {
        let groups = {};
        categories.forEach((cat) => {
            let splits = cat.category_name.split('/');
            let prod = splits[0] ? splits[0] : splits[1];
            if (!groups[prod]) {
                groups[prod] = [];
            }
            groups[prod].push(cat);
        });
        return groups;
    };

    const handleSearch = (term) => {
        setSearchParams({ term });
    };

    const handleAddSuccess = async (category, navigateToPage = false) => {
        if (category && navigateToPage) {
            navigate(`/category?url=${category.category_name}`);
            return;
        }
        setShowAddModal(false);
        let sites = await getCategories();
        setCategories(sites);
    };
    const handleAddCancel = () => {
        setShowAddModal(false);
    };
    const handleAddError = (error) => {
        Alert.error(error.message);
        console.log(error);
    };

    if (!isContentAdmin) {
        return <UnAuthView />;
    }

    return (
        <Fragment>
            <HomeNav />

            <div className="container">
                <Helmet>
                    <title>Categories</title>
                </Helmet>

                <h1>Categories</h1>

                <p>
                    <Link to="/drafts" className="button">
                        Manage Drafts
                    </Link>
                </p>

                <p>
                    <button className="button" onClick={() => setShowAddModal(true)}>
                        Add Category
                    </button>
                </p>

                <Modal visible={showAddModal} onClose={() => setShowAddModal(false)}>
                    <AddCategory onSuccess={handleAddSuccess} onError={handleAddError} onCancel={handleAddCancel} />
                </Modal>

                <h2 className="h4">Quick Links</h2>
                <ul className="list-flat">
                    <li>
                        <Link to="/category?url=/shop-homepage">SHOP Homepage</Link>
                    </li>
                    <li>
                        <a href="#launchpad-collections">Launchpad Collections</a>
                    </li>
                    <li>
                        <a href="#dynamic-lists">Dynamic Lists</a>
                    </li>
                    {Object.keys(categories).map((key) => (
                        <li key={key}>
                            <a href={`#${key}`}>All {key} Categories</a>
                        </li>
                    ))}
                </ul>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, [col] 1fr)', gridGap: '20px' }}>
                    {navigation.map((section) => (
                        <div key={section.name}>
                            <h3>{section.name}</h3>
                            {section.subnav.map((sn) => {
                                let pages = Object.keys(sn.pages).filter((page) =>
                                    categories?.SHOP?.find((c) => c.category_name === sn.pages[page])
                                );
                                if (!pages.length) return null;
                                return (
                                    <div key={`${section.name}_${sn.name}`} className="spaced-single">
                                        <h6 className="collapse">
                                            <Link to={`/category?url=${sn.url_key}`}>{sn.name}</Link>
                                        </h6>
                                        <ul className="list-flat">
                                            {pages.map((page) => (
                                                <li key={sn.pages[page]}>
                                                    <Link to={`/category?url=${sn.pages[page]}`}>{page}</Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>

                <hr />

                <CatSearch term={term} onReset={() => setSearchParams({})} onSearch={handleSearch} />

                {!!results.length && (
                    <ul className="list-flat">
                        {results
                            .sort((a, b) => sorts.asc(a.category_name, b.category_name))
                            .map((cat) => (
                                <li key={`cat_${cat._id || cat.category_name}`}>
                                    <Link to={`/category?url=${cat.category_name}`}>{cat.category_name}</Link>
                                </li>
                            ))}
                    </ul>
                )}
                {!results.length && (
                    <>
                        <h2 id="dynamic-lists">Dynamic Lists</h2>
                        <ul className="list-flat">
                            {categories?.SHOP?.filter((c) => c.query).map((cat) => (
                                <li key={`cat_${cat._id}`}>
                                    <Link to={`/category?url=${cat.category_name}`}>
                                        {cat.display_name || cat.category_name}
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        <hr />

                        <div id="launchpad-collections" className="row">
                            <div className="col-sm-12 col-md-6">
                                <h2>Launchpad App Collections</h2>
                                <ul className="list-flat">
                                    {categories?.SHOP?.filter((c) =>
                                        c.category_name.includes('/playaway-launchpad/apps/collections')
                                    ).map((cat) => (
                                        <li key={`cat_${cat._id}`}>
                                            <Link to={`/category?url=${cat.category_name}`}>{cat.category_name}</Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="col-sm-12 col-md-6">
                                <h2>Launchpad Video Collections</h2>
                                <ul className="list-flat">
                                    {categories?.SHOP?.filter((c) =>
                                        c.category_name.includes('/playaway-launchpad/video/collections')
                                    ).map((cat) => (
                                        <li key={`cat_${cat._id}`}>
                                            <Link to={`/category?url=${cat.category_name}`}>{cat.category_name}</Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <hr />

                        {Object.keys(categories).map((key) => {
                            let groups = groupCategories(categories?.[key]);
                            return (
                                <div key={key} className="mb-10">
                                    <h2 id={key}>{key} Categories</h2>
                                    {Object.keys(groups)
                                        .filter((c) => c !== 'shop-homepage')
                                        .map((key) => (
                                            <Fragment key={key}>
                                                <h4>{key}</h4>
                                                <ul className="list-flat">
                                                    {groups[key]
                                                        .sort((a, b) => sorts.asc(a.category_name, b.category_name))
                                                        .map((cat) => (
                                                            <li key={`cat_${cat._id || cat.category_name}`}>
                                                                <Link to={`/category?url=${cat.category_name}`}>
                                                                    {cat.category_name}
                                                                </Link>
                                                            </li>
                                                        ))}
                                                </ul>
                                            </Fragment>
                                        ))}
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
        </Fragment>
    );
};

export default CategoryList;
